import React from 'react';
import { cn } from '../../utils/cn';

/**
 * Stacked record card used below `md` in place of a wide data table.
 * Pages render the same rows twice: `<table className="hidden md:table">`
 * for pointer-sized screens and a `<DataCard>` list for phones.
 */
export function DataCard({
  children,
  onClick,
  className,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-900',
        onClick && 'cursor-pointer active:bg-slate-50 dark:active:bg-slate-800',
        className
      )}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

/** Label/value pair, label on the left and value right-aligned. */
export function DataCardRow({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex items-start justify-between gap-3 py-1 text-sm', className)}>
      <span className="shrink-0 text-xs font-medium text-slate-500 dark:text-slate-400">{label}</span>
      <span className="min-w-0 break-words text-right text-slate-800 dark:text-slate-200">{children}</span>
    </div>
  );
}

/** Action row pinned to the bottom of a card; buttons share the width evenly. */
export function DataCardActions({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3 [&>*]:flex-1 dark:border-slate-800">
      {children}
    </div>
  );
}

/** Empty / loading placeholder matching the card list rhythm. */
export function DataCardEmpty({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-200 py-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
      {children}
    </div>
  );
}
